import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Security,
  Token,
  EmojiEvents,
  ContentCopy,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useWeb3 } from '../contexts/Web3Context';
import { useAuth } from '../contexts/AuthContext';

const Web3Page: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('web3');
  
  const { 
    account, 
    isConnected, 
    connectWallet, 
    disconnectWallet, 
    balance, 
    chainId 
  } = useWeb3();
  const { user } = useAuth();
  
  const [copied, setCopied] = useState(false);

  const handleCopyAddress = () => {
    if (account) {
      navigator.clipboard.writeText(account);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getNetworkName = (chainId: number | null) => {
    const networks: { [key: number]: string } = {
      1: 'Ethereum Mainnet',
      137: 'Polygon Mainnet',
      1337: 'Local Testnet',
      5: 'Goerli Testnet',
      80001: 'Mumbai Testnet',
    };
    return chainId ? networks[chainId] || `${t('web3.unknownNetwork')} (${chainId})` : t('web3.notConnected');
  };

  const mockUserTokens = [
    { name: 'UAI Token', symbol: 'UAI', balance: '1,250', value: '$125.00' },
    { name: 'Learning Credits', symbol: 'LC', balance: '850', value: '$85.00' },
  ];

  const mockNFTCertificates = [
    { 
      id: 1, 
      titleKey: 'web3.certificatesList.web3Basics', 
      issueDate: '2024-01-15', 
      status: 'active' 
    },
    { 
      id: 2, 
      titleKey: 'web3.certificatesList.aiBasics', 
      issueDate: '2024-02-20', 
      status: 'active' 
    },
    { 
      id: 3, 
      titleKey: 'web3.certificatesList.smartContracts', 
      issueDate: '2024-03-10', 
      status: 'pending' 
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom>
            {t('web3.title')}
          </Typography>
          <Typography variant="h6">
            {t('web3.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Wallet Connection */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {t('web3.wallet.title')}
            </Typography>
            
            {!isConnected ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AccountBalanceWallet sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('web3.wallet.connectTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('web3.wallet.connectDescription')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={connectWallet}
                  startIcon={<AccountBalanceWallet />}
                >
                  {t('web3.wallet.connectMetaMask')}
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  {t('web3.wallet.connected')}
                </Alert>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('web3.wallet.accountInfo')}
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('web3.wallet.address')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontFamily="monospace">
                              {account}
                            </Typography>
                            <Button
                              size="small"
                              onClick={handleCopyAddress}
                              startIcon={<ContentCopy />}
                            >
                              {copied ? t('web3.wallet.copied') : t('web3.wallet.copy')}
                            </Button>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('web3.wallet.ethBalance')}
                          </Typography>
                          <Typography variant="h6">
                            {parseFloat(balance).toFixed(4)} ETH
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('web3.wallet.network')}
                          </Typography>
                          <Chip 
                            label={getNetworkName(chainId)} 
                            color="primary" 
                            size="small" 
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('web3.wallet.quickActions')}
                      </Typography>
                      <Stack spacing={2}>
                        <Button variant="outlined" fullWidth>
                          {t('web3.wallet.viewTransactions')}
                        </Button>
                        <Button variant="outlined" fullWidth>
                          {t('web3.wallet.addToken')}
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          fullWidth
                          onClick={disconnectWallet}
                        >
                          {t('web3.wallet.disconnect')}
                        </Button>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {isConnected && (
          <>
            {/* Token Assets */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Token />
                  {t('web3.tokens.title')}
                </Typography>
                
                <Grid container spacing={3}>
                  {mockUserTokens.map((token, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{token.name}</Typography>
                            <Chip label={token.symbol} color="primary" size="small" />
                          </Box>
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            {token.balance}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('web3.tokens.value')}: {token.value}
                          </Typography>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined">
                              {t('web3.tokens.earn')}
                            </Button>
                            <Button size="small" variant="outlined">
                              {t('web3.tokens.spend')}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* NFT Certificates */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmojiEvents />
                  {t('web3.certificates.title')}
                </Typography>
                
                <Grid container spacing={3}>
                  {mockNFTCertificates.map((cert) => (
                    <Grid item xs={12} md={4} key={cert.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Chip 
                              label={cert.status === 'active' ? t('web3.certificates.active') : t('web3.certificates.pending')} 
                              color={cert.status === 'active' ? 'success' : 'warning'} 
                              size="small" 
                            />
                          </Box>
                          <Typography variant="h6" gutterBottom>
                            {t(cert.titleKey)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('web3.certificates.issueDate')}: {cert.issueDate}
                          </Typography>
                          <Button variant="outlined" size="small" fullWidth>
                            {t('web3.certificates.view')}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Learning Stats */}
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security />
                  {t('web3.stats.title')}
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {mockNFTCertificates.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('web3.stats.totalCertificates')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {mockUserTokens.reduce((acc, token) => acc + parseInt(token.balance.replace(',', '')), 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('web3.stats.totalTokens')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        95%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('web3.stats.completionRate')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        A+
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('web3.stats.overallGrade')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
};

export default Web3Page; 